import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { TextShimmer } from "./text-shimmer";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 [&_svg]:transition-colors [&_svg]:duration-300 [&_svg]:ease-in-out outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-98 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:cursor-pointer",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 hover:cursor-pointer",
        outline:
          "border border-transparent bg-[var(--bw)] hover:border-foreground text-muted-foreground hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50 rounded-xl hover:cursor-pointer",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 hover:cursor-pointer",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline hover:cursor-pointer",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-xl gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-xl px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  shimmerText,
  shimmerClassName,
  shimmerStyle,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    shimmerText?: string;
    shimmerClassName?: string;
    shimmerStyle?: React.CSSProperties;
  }) {
  const Comp = asChild ? Slot : "button";

  // Мемоизируем стиль для TextShimmer, чтобы предотвратить сброс анимации
  const defaultShimmerStyle = useMemo<React.CSSProperties>(
    () =>
      ({
        "--base-color": "var(--status-info-text)",
        "--base-gradient-color": "var(--status-info-text)",
        "--bg": "linear-gradient(90deg, var(--status-info-text) calc(50% - var(--spread)), #D9EBFF, var(--status-info-text) calc(50% + var(--spread)))",
      } as React.CSSProperties),
    []
  );

  const shimmerStyles = shimmerStyle || defaultShimmerStyle;

  const { style: propsStyle, ...restProps } = props;

  const content = shimmerText ? (
    <TextShimmer
      className={shimmerClassName}
      style={shimmerStyles}
    >
      {shimmerText}
    </TextShimmer>
  ) : (
    children
  );

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      style={propsStyle}
      {...restProps}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants };
